import { Link } from "react-router-dom";
import {
    Sprout, ArrowRight, MessageCircle, Grid3X3, Calendar,
    Bug, BookOpen, Share2, Check, Sparkles, FileText, Leaf,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import TestimonialsSection from "@/components/TestimonialsSection";

/**
 * Public marketing / About page.
 *
 * This is the first page anyone lands on — it has to sell the product in
 * one glance, explain WHAT rooteed does, WHY it's helpful, and show
 * enough real garden photography that prospective users can picture
 * themselves using it. Every CTA points to either /register (new) or
 * /login (returning, incl. the "Try the demo" button).
 *
 * Photos are sourced from Unsplash (free-to-use, verified live) so the
 * page feels grounded in real gardens, not stock illustrations.
 */

const HERO_PHOTO = "https://images.unsplash.com/photo-1464297162577-f5295c892194?w=1600&h=1000&fit=crop&auto=format";
const AI_PHOTO = "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=900&h=700&fit=crop&auto=format";
const GRID_PHOTO = "https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=900&h=700&fit=crop&auto=format";
const HARVEST_PHOTO = "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=900&h=700&fit=crop&auto=format";
const WATERING_PHOTO = "https://images.unsplash.com/photo-1515150144380-bca9f1650ed9?w=700&h=500&fit=crop&auto=format";
const SEEDLING_PHOTO = "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=700&h=500&fit=crop&auto=format";
const JOURNAL_PHOTO = "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=700&h=500&fit=crop&auto=format";

const FEATURES = [
    {
        icon: MessageCircle,
        title: "A mentor who actually tends your garden",
        body: "Chat with rooteed in plain English — 'Plan me a 2 × 1 m raised bed for tomatoes and basil' — and it proposes a concrete plan you review and tap once to apply. Beds, crops, harvest logs, reorganisation. All through conversation.",
        photo: AI_PHOTO,
        photoAlt: "A hand gently tending to young seedlings in a propagation tray",
        testid: "feature-ai-mentor",
    },
    {
        icon: Grid3X3,
        title: "A visual bed designer that thinks ahead",
        body: "Drag crops into a live grid. rooteed highlights antagonist neighbours before you drop, suggests companion pairings, auto-splits overcrowded plantings into follow-up succession crops, and respects your rotation family history.",
        photo: GRID_PHOTO,
        photoAlt: "Overhead view of seedlings arranged in a grid of individual pots",
        testid: "feature-bed-designer",
    },
    {
        icon: Calendar,
        title: "Every crop, from seed to harvest, in one journal",
        body: "Sow dates, transplant windows, expected yield ranges, real harvest weights. Your season becomes a searchable timeline, and at the end of the year rooteed rolls it into a printable PDF report you can keep on the shelf.",
        photo: HARVEST_PHOTO,
        photoAlt: "Freshly harvested vegetables — cucumbers, carrots and peppers — at a market",
        testid: "feature-journal",
    },
];

const STEPS = [
    {
        n: "01",
        title: "Plant your first seed",
        body: "Sign up (or tap Try the Demo) and rooteed auto-seeds a starter garden so you can explore without setup.",
        photo: SEEDLING_PHOTO,
        photoAlt: "Young seedlings germinating in a biodegradable tray",
    },
    {
        n: "02",
        title: "Tell the mentor what you want",
        body: "Ask it to build beds, plant crops, adjust spacing. Every suggestion arrives as an Apply-in-one-tap plan card.",
        photo: WATERING_PHOTO,
        photoAlt: "A watering can gently showering water onto a row of young plants",
    },
    {
        n: "03",
        title: "Track the season, celebrate the harvest",
        body: "Log sowings and harvests, watch the visual grid mature, and share a beautiful Harvest Party card with friends.",
        photo: JOURNAL_PHOTO,
        photoAlt: "Gardening tools and soil laid out for a planting session",
    },
];

const BULLETS_FREE = [
    "1 garden, 3 beds, 20 crops",
    "AI chat with plan cards",
    "Visual bed designer",
    "Timeline + harvest log",
    "Weather-aware alerts",
];
const BULLETS_PLUS = [
    "Unlimited gardens, beds, crops",
    "Priority AI responses",
    "Up to 100 garden photos",
    "Season PDF report",
    "Harvest Party share cards",
    "Pests & beneficials library",
    "Public share links",
    "Book-notes interpreter (150 MB PDFs)",
];

function Pill({ children }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[var(--vm-border)] bg-[var(--vm-card)]/60 text-[10px] uppercase tracking-[0.18em] text-[var(--vm-sage)]">
            {children}
        </span>
    );
}

export default function About() {
    return (
        <div className="min-h-screen paper-texture" data-testid="about-page">
            {/* ---- Top nav (public) ---- */}
            <nav className="max-w-6xl mx-auto px-6 pt-8 pb-4 flex items-center justify-between">
                <Link to="/about" className="flex items-center gap-3 group" data-testid="about-nav-home">
                    <Logo variant="icon" size={48} />
                    <div>
                        <div className="font-serif-display text-2xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-0.5">Garden Journal</div>
                    </div>
                </Link>
                <div className="flex items-center gap-3">
                    <Link to="/pricing" className="text-sm text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)] px-3 py-2" data-testid="about-nav-pricing">
                        Pricing
                    </Link>
                    <Link to="/login" className="text-sm text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)] px-3 py-2" data-testid="about-nav-login">
                        Sign in
                    </Link>
                    <Link to="/register">
                        <Button
                            data-testid="about-nav-register"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-5"
                        >
                            Start free
                        </Button>
                    </Link>
                </div>
            </nav>

            {/* ---- Hero ---- */}
            <section className="max-w-6xl mx-auto px-6 pt-12 pb-20 grid md:grid-cols-2 gap-10 items-center" data-testid="about-hero">
                <div>
                    <Pill>
                        <Sparkles className="w-3 h-3" /> Powered by Claude Sonnet 4.5
                    </Pill>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[var(--vm-text)] leading-[1.02] mt-6">
                        Your quiet, <span className="italic text-[var(--vm-accent)]">garden-aware</span> companion — from seed to harvest.
                    </h1>
                    <p className="text-base text-[var(--vm-text-secondary)] mt-6 max-w-xl leading-relaxed">
                        rooteed is an AI copilot built for home vegetable gardeners. Plan beds by chatting in plain
                        English, drop crops into a visual grid that warns about bad neighbours, and keep every sow
                        date, harvest weight and book note in one warm journal.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Link to="/register">
                            <Button
                                data-testid="about-hero-register-btn"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6 py-6 text-base"
                            >
                                Plant your first seed <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button
                                variant="outline"
                                data-testid="about-hero-demo-btn"
                                className="rounded-full border-[var(--vm-sage)] text-[var(--vm-sage)] hover:bg-[var(--vm-sage)]/10 px-6 py-6 text-base"
                            >
                                <Sprout className="w-4 h-4 mr-2" /> Try the demo garden
                            </Button>
                        </Link>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-xs text-[var(--vm-text-muted)]">
                        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--vm-sage)]" /> Free tier forever</span>
                        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--vm-sage)]" /> No credit card required</span>
                        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--vm-sage)]" /> Built for Ireland & temperate climates</span>
                    </div>
                </div>
                <div className="relative">
                    <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-[var(--vm-border)] shadow-xl">
                        <img
                            src={HERO_PHOTO}
                            alt="Ripening green tomatoes on the vine, bathed in golden afternoon sunlight"
                            className="w-full h-full object-cover"
                            loading="eager"
                            data-testid="about-hero-photo"
                        />
                    </div>
                    <div className="absolute -bottom-6 -left-6 bg-[var(--vm-card)] border border-[var(--vm-border)] rounded-xl p-4 shadow-lg max-w-[260px] hidden md:block">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--vm-sage)] mb-1">From the mentor</div>
                        <p className="text-sm text-[var(--vm-text)] italic leading-snug">
                            “Frost is forecast Wednesday — cover the tomatoes after sunset, I'll remind you again Tuesday evening.”
                        </p>
                    </div>
                </div>
            </section>

            {/* ---- Features ---- */}
            <section className="max-w-6xl mx-auto px-6 py-20 space-y-24" data-testid="about-features">
                <div className="max-w-2xl">
                    <Pill>What it does</Pill>
                    <h2 className="font-serif-display text-4xl lg:text-5xl text-[var(--vm-text)] mt-4">
                        Three things that make rooteed different.
                    </h2>
                </div>

                {FEATURES.map((f, i) => {
                    const Icon = f.icon;
                    const reverse = i % 2 === 1;
                    return (
                        <div
                            key={f.title}
                            data-testid={f.testid}
                            className={`grid md:grid-cols-2 gap-10 items-center ${reverse ? "md:[&>div:first-child]:order-2" : ""}`}
                        >
                            <div>
                                <div className="w-12 h-12 rounded-full bg-[var(--vm-accent)]/12 flex items-center justify-center mb-5">
                                    <Icon className="w-5 h-5 text-[var(--vm-accent)]" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif-display text-3xl lg:text-4xl text-[var(--vm-text)] leading-tight">
                                    {f.title}
                                </h3>
                                <p className="text-base text-[var(--vm-text-secondary)] mt-4 leading-relaxed max-w-lg">
                                    {f.body}
                                </p>
                            </div>
                            <div className="aspect-[5/4] rounded-2xl overflow-hidden border border-[var(--vm-border)] shadow-md">
                                <img src={f.photo} alt={f.photoAlt} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </div>
                    );
                })}
            </section>

            {/* ---- Extra capabilities strip ---- */}
            <section className="max-w-6xl mx-auto px-6 py-16" data-testid="about-capabilities">
                <div className="max-w-2xl mb-10">
                    <Pill>And a whole lot more</Pill>
                    <h2 className="font-serif-display text-3xl lg:text-4xl text-[var(--vm-text)] mt-4">
                        A full garden workshop, tucked into a quiet journal.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                        { icon: Bug, title: "Pests & beneficials library", body: "Real photos of the bugs you'll meet — the troublemakers and the allies." },
                        { icon: BookOpen, title: "Book notes interpreter", body: "Paste or upload notes from your favourite gardening books; rooteed maps them to your beds." },
                        { icon: Share2, title: "Share your garden", body: "Public read-only links so friends and neighbours can peek at what you've planted." },
                        { icon: FileText, title: "Season PDF report", body: "A printable journal of your year — every bed, crop, harvest, insight." },
                    ].map((c) => {
                        const Icon = c.icon;
                        return (
                            <div
                                key={c.title}
                                data-testid={`capability-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="journal-card p-6"
                            >
                                <Icon className="w-5 h-5 text-[var(--vm-accent)] mb-3" strokeWidth={1.5} />
                                <h4 className="font-serif-display text-xl text-[var(--vm-text)] leading-tight">{c.title}</h4>
                                <p className="text-sm text-[var(--vm-text-secondary)] mt-2 leading-relaxed">{c.body}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ---- Testimonials (auto-hides until approved entries exist) ---- */}
            <TestimonialsSection />

            {/* ---- How it works ---- */}
            <section className="max-w-6xl mx-auto px-6 py-20" data-testid="about-how-it-works">
                <div className="max-w-2xl mb-12">
                    <Pill>How it works</Pill>
                    <h2 className="font-serif-display text-4xl lg:text-5xl text-[var(--vm-text)] mt-4">
                        Three steps. No spreadsheets.
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                    {STEPS.map((s) => (
                        <div key={s.n} data-testid={`step-${s.n}`} className="flex flex-col">
                            <div className="aspect-[4/3] rounded-xl overflow-hidden border border-[var(--vm-border)] mb-5 shadow-sm">
                                <img src={s.photo} alt={s.photoAlt} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="text-xs uppercase tracking-[0.25em] text-[var(--vm-accent)] font-semibold">Step {s.n}</div>
                            <h3 className="font-serif-display text-2xl text-[var(--vm-text)] mt-2 leading-snug">{s.title}</h3>
                            <p className="text-sm text-[var(--vm-text-secondary)] mt-2 leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ---- Pricing teaser ---- */}
            <section className="max-w-6xl mx-auto px-6 py-20" data-testid="about-pricing">
                <div className="max-w-2xl mb-10">
                    <Pill>Simple pricing</Pill>
                    <h2 className="font-serif-display text-4xl lg:text-5xl text-[var(--vm-text)] mt-4">
                        Free forever. Plus is €2 / month.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="journal-card p-8" data-testid="tier-free-card">
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)]">Free</div>
                        <div className="mt-3 flex items-baseline gap-1">
                            <span className="font-serif-display text-5xl text-[var(--vm-text)]">€0</span>
                            <span className="text-[var(--vm-text-muted)]">/ forever</span>
                        </div>
                        <p className="text-sm text-[var(--vm-text-secondary)] mt-3">Enough to plan and track one real garden. Ideal for first-year gardeners.</p>
                        <ul className="space-y-2 mt-6">
                            {BULLETS_FREE.map((b) => (
                                <li key={b} className="flex items-start gap-2 text-sm text-[var(--vm-text)]">
                                    <Check className="w-4 h-4 text-[var(--vm-sage)] shrink-0 mt-0.5" strokeWidth={2} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/register" className="block mt-7">
                            <Button
                                variant="outline"
                                data-testid="tier-free-cta"
                                className="w-full rounded-full border-[var(--vm-border)] hover:bg-[var(--vm-input)]"
                            >
                                Start free
                            </Button>
                        </Link>
                    </div>

                    <div className="journal-card p-8 border-[var(--vm-accent)]/40 bg-[var(--vm-card)]" data-testid="tier-plus-card">
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-accent)] font-semibold flex items-center gap-2">
                            <Sparkles className="w-3 h-3" /> Plus
                        </div>
                        <div className="mt-3 flex items-baseline gap-1">
                            <span className="font-serif-display text-5xl text-[var(--vm-text)]">€2</span>
                            <span className="text-[var(--vm-text-muted)]">/ month</span>
                        </div>
                        <p className="text-sm text-[var(--vm-text-secondary)] mt-3">Everything unlocked — for when one garden becomes a proper hobby.</p>
                        <ul className="space-y-2 mt-6">
                            {BULLETS_PLUS.map((b) => (
                                <li key={b} className="flex items-start gap-2 text-sm text-[var(--vm-text)]">
                                    <Check className="w-4 h-4 text-[var(--vm-accent)] shrink-0 mt-0.5" strokeWidth={2} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/pricing" className="block mt-7">
                            <Button
                                data-testid="tier-plus-cta"
                                className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                            >
                                See Plus details <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ---- Final CTA ---- */}
            <section className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="about-final-cta">
                <h2 className="font-serif-display text-4xl lg:text-5xl text-[var(--vm-text)] leading-tight">
                    Ready to plant your first seed?
                </h2>
                <p className="text-base text-[var(--vm-text-secondary)] mt-5 max-w-2xl mx-auto">
                    Sign up in under a minute, or skip straight into a ready-made demo allotment
                    with 6 beds, 21 crops and a full season of notes — no account needed.
                </p>
                <div className="flex flex-wrap justify-center gap-3 mt-8">
                    <Link to="/register">
                        <Button
                            data-testid="about-final-register-btn"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-7 py-6 text-base"
                        >
                            Create your account <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/login">
                        <Button
                            variant="outline"
                            data-testid="about-final-demo-btn"
                            className="rounded-full border-[var(--vm-sage)] text-[var(--vm-sage)] hover:bg-[var(--vm-sage)]/10 px-7 py-6 text-base"
                        >
                            <Sprout className="w-4 h-4 mr-2" /> Try the demo garden
                        </Button>
                    </Link>
                </div>
            </section>

            {/* ---- Footer ---- */}
            <footer className="max-w-6xl mx-auto px-6 py-10 border-t border-[var(--vm-border)] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[var(--vm-text-muted)]">
                <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-[var(--vm-sage)]" strokeWidth={1.5} />
                    <span>© {new Date().getFullYear()} rooteed · A quiet journal for home gardeners.</span>
                </div>
                <div className="flex items-center gap-4">
                    <Link to="/pricing" className="hover:text-[var(--vm-text)]">Pricing</Link>
                    <Link to="/login" className="hover:text-[var(--vm-text)]">Sign in</Link>
                    <Link to="/register" className="hover:text-[var(--vm-text)]">Register</Link>
                </div>
            </footer>
        </div>
    );
}
